import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import { getActiveTenantId } from "@/shared/tenant/active-tenant";
import type { AiProfile, AiSkill, BrandKit, PreviewResult, Surface } from "./types";

// Query keys are tenant-scoped so switching tenant refetches (ADR-0016).
const key = (...parts: string[]) => ["ai-profile", ...parts, getActiveTenantId()] as const;

export function useAiProfile() {
  return useQuery({ queryKey: key("profile"), queryFn: () => httpClient<AiProfile>("/api/ai-profile") });
}

export function useUpdateAiProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<AiProfile>) =>
      httpClient<AiProfile>("/api/ai-profile", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-profile", "profile"] });
      qc.invalidateQueries({ queryKey: ["ai-profile", "preview"] });
    },
  });
}

export function useBrandKit() {
  return useQuery({ queryKey: key("brand"), queryFn: () => httpClient<BrandKit>("/api/ai-profile/brand") });
}

export function useUpdateBrandKit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<BrandKit>) =>
      httpClient<BrandKit>("/api/ai-profile/brand", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-profile", "brand"] });
      qc.invalidateQueries({ queryKey: ["ai-profile", "preview"] });
    },
  });
}

export function useSkills() {
  return useQuery({ queryKey: key("skills"), queryFn: () => httpClient<AiSkill[]>("/api/ai-profile/skills") });
}

export function useCreateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: Partial<AiSkill> & { name: string; instructions: string }) =>
      httpClient<AiSkill>("/api/ai-profile/skills", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(dto),
      }),
    onSuccess: () => invalidateSkills(qc),
  });
}

export function useUpdateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<AiSkill> }) =>
      httpClient<AiSkill>(`/api/ai-profile/skills/${id}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      }),
    onSuccess: () => invalidateSkills(qc),
  });
}

export function useDeleteSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => httpClient<{ id: string }>(`/api/ai-profile/skills/${id}`, { method: "DELETE" }),
    onSuccess: () => invalidateSkills(qc),
  });
}

/** The composed tenant block for a surface — the live preview of the effect. */
export function usePreview(surface: Surface, enabled = true) {
  return useQuery({
    queryKey: key("preview", surface),
    queryFn: () => httpClient<PreviewResult>(`/api/ai-profile/preview?surface=${surface}`),
    enabled,
  });
}

function invalidateSkills(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["ai-profile", "skills"] });
  qc.invalidateQueries({ queryKey: ["ai-profile", "preview"] });
}
